type Point = number | null

export interface LineSeries {
  readonly label?: string
  readonly color: string
  readonly data: readonly Point[]
  readonly fill?: boolean
  readonly dots?: boolean
}

export interface LineChartProps {
  readonly series: readonly LineSeries[]
  readonly width?: number
  readonly height?: number
  readonly yMax?: number
  readonly xLabels?: readonly string[]
  readonly showGrid?: boolean
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

function linePath(data: readonly Point[], xScale: (index: number) => number, yScale: (value: number) => number) {
  const commands: string[] = []
  data.forEach((point, index) => {
    if (point === null) {
      return
    }
    const command = commands.length === 0 || data[index - 1] === null ? 'M' : 'L'
    commands.push(`${command}${xScale(index)},${yScale(point)}`)
  })
  return commands.join('')
}

function lineSegments(data: readonly Point[], xScale: (index: number) => number, yScale: (value: number) => number) {
  const segments: string[] = []
  let current: string[] = []
  data.forEach((point, index) => {
    if (point === null) {
      if (current.length > 0) {
        segments.push(current.join(''))
        current = []
      }
      return
    }
    const command = current.length === 0 ? 'M' : 'L'
    current.push(`${command}${xScale(index)},${yScale(point)}`)
  })
  if (current.length > 0) segments.push(current.join(''))
  return segments
}

export function LineChart({
  series,
  width = 600,
  height = 220,
  yMax: yMaxOverride,
  xLabels,
  showGrid = true,
}: LineChartProps) {
  const padding = { left: 36, right: 16, top: 16, bottom: 28 }
  const allValues = series.flatMap((entry) => entry.data.filter((value): value is number => value !== null))
  if (allValues.length === 0) return null
  const yMin = 0
  const yMax = yMaxOverride ?? Math.ceil(Math.max(...allValues) * 1.1)
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom
  const xCount = series[0]?.data.length ?? 0
  const xStep = xCount > 1 ? innerWidth / (xCount - 1) : 0
  const xScale = (index: number) => padding.left + index * xStep
  const yScale = (value: number) => padding.top + innerHeight - ((clamp(value, yMin, yMax) - yMin) / (yMax - yMin)) * innerHeight
  const ticks = Array.from({ length: 5 }, (_, index) => yMin + ((yMax - yMin) / 4) * index)

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', width: '100%', height: 'auto', maxWidth: width }}
      aria-hidden="true"
    >
      {showGrid
        ? ticks.map((tick) => (
            <g key={tick}>
              <line x1={padding.left} x2={width - padding.right} y1={yScale(tick)} y2={yScale(tick)} stroke="var(--divider)" strokeWidth={1} />
              <text x={padding.left - 8} y={yScale(tick) + 3} textAnchor="end" fontSize="10" fill="var(--fg-subtle)" fontFamily="var(--font-mono)">
                {Math.round(tick)}
              </text>
            </g>
          ))
        : null}
      {xLabels?.map((label, index) => (
        <text key={label} x={xScale(index)} y={height - padding.bottom + 14} textAnchor="middle" fontSize="10" fill="var(--fg-muted)" fontFamily="var(--font-mono)">
          {label}
        </text>
      ))}
      {series.map((entry, seriesIndex) => {
        const path = linePath(entry.data, xScale, yScale)
        const segments = lineSegments(entry.data, xScale, yScale)
        return (
          <g key={entry.label ?? seriesIndex}>
            {entry.fill
              ? segments.map((segment, segmentIndex) => (
                  <path
                    key={`${seriesIndex}-${segmentIndex}`}
                    data-part="fill"
                    d={`${segment}L${xScale(entry.data.length - 1)},${padding.top + innerHeight}L${xScale(0)},${padding.top + innerHeight}Z`}
                    fill={entry.color}
                    opacity={0.08}
                  />
                ))
              : null}
            <path
              data-part="line"
              d={path}
              stroke={entry.color}
              fill="none"
              strokeWidth={1.75}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {entry.dots
              ? entry.data.map((point, index) =>
                  point === null ? null : (
                    <circle key={index} cx={xScale(index)} cy={yScale(point)} r={2.5} fill={entry.color} />
                  ),
                )
              : null}
          </g>
        )
      })}
    </svg>
  )
}
